// Compare (SCENARIOS E3, PLAN-2026-10-03-research-tools.md §9): two to four
// players side by side, in this league's scoring, and their seasons week by
// week (B-376). `GET /api/leagues/:id/compare?players=a,b,c,d`.
//
// THE NUMBERS BESIDE EACH OTHER, NEVER A PICK (docs/decisions/2026-09-12-tools-
// not-instructions.md, cohesion check 9). The higher figure in each row is
// named in `highest` so every client marks the same one, and nothing here says
// which player to choose.

import { z } from 'zod';
import { MatchupCue } from './matchup-cue';
import { PlanCell } from './plan';
import { LogColumn } from './player-weeks';
import { Availability, GameStatus, PlayerSummary, ProjectionBasis } from './views';

/**
 * Up to four is the comparison; one is where it starts, from the card. Four
 * since 2026-10-17 (B-375): the third-party comparisons take three to five,
 * and four sit side by side from a tablet up.
 */
export const COMPARE_MAX = 4;

/** A week of his season: what it scored, or which kind of nothing, and against whom. */
export const CompareWeek = z.object({
  period: z.number().int().min(1),
  label: z.string(),
  /** The card's season log's state for the week (`PlayerWeekRow.state`), less `upcoming`. */
  state: z.enum(['played', 'bye', 'no-line', 'pending']),
  /** In the scoring the week was played under; null unless `played`. */
  points: z.number().nullable(),
  /** The club faced, when there was a game (`PlayerWeekRow.opponent`). Added 2026-10-17. */
  opponent: z.string().nullable().default(null),
  home: z.boolean().nullable().default(null),
  /**
   * The week's numbers by stat (`PlayerWeekRow.stats`): every column of his
   * game log the line has. Null unless `played`. Added 2026-10-17 (B-376).
   */
  stats: z.record(z.string(), z.number()).nullable().default(null),
});
export type CompareWeek = z.infer<typeof CompareWeek>;

/**
 * His next bye from this week to the end of the league's season:
 * - `week`: that week.
 * - `none`: every week left is on the calendar and his club plays in all of them.
 * - `unknown`: no bye in the weeks on the calendar, and some are not on it yet.
 * Null for a player with no club.
 */
export const CompareBye = z.discriminatedUnion('state', [
  z.object({ state: z.literal('week'), period: z.number().int().min(1), label: z.string() }),
  z.object({ state: z.literal('none') }),
  z.object({ state: z.literal('unknown') }),
]);
export type CompareBye = z.infer<typeof CompareBye>;

export const ComparePlayer = z.object({
  player: PlayerSummary,
  /** Who holds him in this league. */
  inLeague: Availability,
  /** This week's game, as the Players row has it. */
  game: GameStatus.nullable(),
  /** This week's matchup cue (`MatchupCue`), null before the opponent has played. */
  matchup: MatchupCue.nullable(),
  /** This week's projection in this league's scoring, as the card serves it. */
  projection: z.number().nullable(),
  basis: ProjectionBasis.nullable(),
  /** Weeks with a line this season, the season log's count. */
  games: z.number().int().min(0),
  /** Points a game over those weeks, null with none. */
  perGame: z.number().nullable(),
  /** Points a game over the last three of them (fewer when he has fewer), null with none. Like `perGame`, the season log's arithmetic. */
  lastThree: z.number().nullable(),
  /** His points over those weeks, the season log's total; null with none. Added 2026-10-17. */
  points: z.number().nullable().default(null),
  /**
   * Each column of his game log over those weeks, keyed by column, as the
   * card's totals row reads them (`PlayerWeeksView.summary.totals`): the
   * season's line under the week-by-week table. Added 2026-10-17 (B-376).
   */
  totals: z.record(z.string(), z.number()).default({}),
  /**
   * His own columns of `CompareView.columns`, by key: his position's stat
   * sheet, the card's game log's (B-515, B-517). A column of another player's position is no
   * figure of his, and a client leaves his cell in it empty rather than
   * reading a kicker as a quarterback's dashes. Added 2026-10-26.
   */
  columns: z.array(z.string()).default([]),
  /**
   * Every week of the season that has begun, oldest first: the season log's.
   * A week whose game is still ahead of him is not here (a Sunday player on
   * a Thursday night), so a client reads a week by its period.
   */
  weeks: z.array(CompareWeek),
  /** His highest and lowest scoring week this season, null with none. */
  high: CompareWeek.nullable(),
  low: CompareWeek.nullable(),
  /** The weeks after this one in `CompareView.ahead`, a cell each, the planner's cell. */
  ahead: z.array(PlanCell),
  bye: CompareBye.nullable(),
});
export type ComparePlayer = z.infer<typeof ComparePlayer>;

/** The figures a comparison marks the highest of. */
export const CompareFigure = z.enum(['projection', 'perGame', 'lastThree', 'high', 'low']);
export type CompareFigure = z.infer<typeof CompareFigure>;

export const CompareView = z.object({
  leagueId: z.string(),
  /** The week the projection, game and matchup are for: the one being played, or next. */
  week: z.object({ period: z.number().int().min(1), label: z.string() }).nullable(),
  /** The weeks after it that each player's `ahead` covers, up to three. */
  ahead: z.array(z.object({ period: z.number().int().min(1), label: z.string() })),
  /** In the order asked. */
  players: z.array(ComparePlayer).min(1).max(COMPARE_MAX),
  /**
   * The week-by-week table's stat columns (B-376): every column any of the
   * players' game logs has, each player's in his sport's order, a later
   * player's new ones after the column they follow in his own. A player's
   * week has a number only in his own. Added 2026-10-17.
   */
  columns: z.array(LogColumn).default([]),
  /**
   * The weeks of the table, oldest first: every week any of the players has
   * (`ComparePlayer.weeks`), whether it was before this league's first, and
   * who scored the most in it — every one on a tie — when at least two have
   * points and they are not all equal, as `highest` marks a figure. Added
   * 2026-10-17 (B-376).
   */
  weeks: z
    .array(
      z.object({
        period: z.number().int().min(1),
        label: z.string(),
        beforeSeason: z.boolean(),
        highest: z.array(z.string()),
      }),
    )
    .default([]),
  /**
   * Who has the most season points (`ComparePlayer.points`), by `highest`'s
   * rule. A field of its own rather than a key of `highest`: a record keyed
   * by `CompareFigure` takes every key and only those, so a new key would
   * fail a client that parses the old shape. Added 2026-10-17 (B-376).
   */
  highestPoints: z.array(z.string()).default([]),
  /**
   * Per figure, the ids holding its highest value — every one of them on a
   * tie — when at least two players have the figure and they are not all
   * equal; empty otherwise. A higher low week is the higher figure too.
   */
  highest: z.record(CompareFigure, z.array(z.string())),
  asOf: z.string(),
});
export type CompareView = z.infer<typeof CompareView>;

/** `?players=a,b,c,d`: one to four distinct ids, in the order asked. */
export const CompareQuery = z.object({
  players: z
    .string()
    .transform((s) => [
      ...new Set(
        s
          .split(',')
          .map((x) => x.trim())
          .filter((x) => x.length > 0),
      ),
    ])
    .pipe(z.array(z.string().min(1).max(64)).min(1).max(COMPARE_MAX)),
});
export type CompareQueryShape = z.infer<typeof CompareQuery>;

/**
 * Who holds the highest value of each figure (`CompareView.highest`). Pure,
 * and the one copy: a figure two players do not both have, or one they all
 * share, marks nobody.
 */
export function highestOf(players: readonly ComparePlayer[]): Record<CompareFigure, string[]> {
  const figure = (p: ComparePlayer, f: CompareFigure): number | null => {
    switch (f) {
      case 'projection':
        return p.projection;
      case 'perGame':
        return p.perGame;
      case 'lastThree':
        return p.lastThree;
      case 'high':
        return p.high?.points ?? null;
      case 'low':
        return p.low?.points ?? null;
    }
  };
  const out = {} as Record<CompareFigure, string[]>;
  for (const f of CompareFigure.options) {
    out[f] = topOf(players.map((p) => ({ id: p.player.id, v: figure(p, f) })));
  }
  return out;
}

/** Who has the most season points (`CompareView.highestPoints`), by the same rule. */
export function highestPointsOf(players: readonly ComparePlayer[]): string[] {
  return topOf(players.map((p) => ({ id: p.player.id, v: p.points })));
}

/**
 * Who holds the top value: every one on a tie, and nobody when fewer than two
 * have one or they are all equal. The one rule `highest`, the season's points
 * and the weeks share.
 */
function topOf(values: readonly { id: string; v: number | null }[]): string[] {
  const have = values.filter((x): x is { id: string; v: number } => x.v !== null);
  const top = Math.max(...have.map((x) => x.v));
  return have.length < 2 || have.every((x) => x.v === top)
    ? []
    : have.filter((x) => x.v === top).map((x) => x.id);
}

/**
 * The week-by-week table's weeks (`CompareView.weeks`): every week any player
 * has, oldest first, with who scored the most in each. Pure, and the one copy.
 */
export function weeksOf(
  players: readonly ComparePlayer[],
  beforeSeason: (period: number) => boolean,
): CompareView['weeks'] {
  const labels = new Map<number, string>();
  for (const p of players) for (const w of p.weeks) labels.set(w.period, w.label);
  return [...labels.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([period, label]) => ({
      period,
      label,
      beforeSeason: beforeSeason(period),
      highest: topOf(
        players.map((p) => {
          const w = p.weeks.find((x) => x.period === period);
          return { id: p.player.id, v: w?.state === 'played' ? w.points : null };
        }),
      ),
    }));
}
